import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Coordinate } from "../../entities/tracking";
import { fetchRoutePolyline } from "./routing";

// Clé de cache stable basée sur les waypoints arrondis (~1 m). Tant que les
// waypoints ne changent pas, AUCUN nouvel appel Routes API n'est fait — la route
// d'un trajet donné est calculée une seule fois (≠ position GPS de l'agent qui,
// elle, bouge en continu via le websocket sans recalcul d'itinéraire).
function waypointsKey(waypoints: Coordinate[]): string {
  return waypoints
    .map((c) => `${c.latitude.toFixed(5)},${c.longitude.toFixed(5)}`)
    .join("|");
}

// Renvoie la polyline routière (qui suit les routes) pour une suite de waypoints.
// `data` est null/undefined tant que ça charge ou si l'appel échoue → l'appelant
// retombe sur la ligne droite entre les points.
export function useRoutePolyline(waypoints: Coordinate[]) {
  return useQuery({
    queryKey: ["route-polyline", waypointsKey(waypoints)],
    queryFn:  ({ signal }) => fetchRoutePolyline(waypoints, signal),
    enabled:  waypoints.length >= 2,
    staleTime: Infinity,          // une route donnée ne change pas
    gcTime:    1000 * 60 * 30,    // 30 min en cache
    retry:     1,
    // ⚠️ On CONSERVE le tracé précédent pendant qu'un nouveau se calcule.
    //
    // Sans cela, chaque recalcul — la Main sort de sa route, elle passe à
    // l'arrêt suivant — vidait `data` le temps de l'aller-retour, et l'appelant
    // retombait sur sa ligne droite de secours. À l'écran : un itinéraire
    // routier qui redevient brutalement un trait, puis se rétablit. Le client
    // voyait cette ligne droite d'autant plus longtemps que le réseau était
    // lent, c'est-à-dire précisément quand il regardait sa carte.
    placeholderData: keepPreviousData,
  });
}
